import { useCallback, useEffect, useRef } from 'react'

import type VirtualBackgroundExtension from 'agora-extension-virtual-background'
import type { IVirtualBackgroundProcessor } from 'agora-extension-virtual-background'
import type { ILocalVideoTrack } from 'agora-rtc-sdk-ng'

import { logError } from '@core/logging'

import { loadAgoraRTC, loadVirtualBackgroundExtension } from '../libs/utils'

export interface UseVirtualBackgroundProps {
  videoTrack?: ILocalVideoTrack
  virtualBackground?: 'blur' | undefined | { type: 'image'; url: string }
}

/**
 * Hook that applies virtual background to local video track
 */
export function useVirtualBackground({
  videoTrack,
  virtualBackground,
}: UseVirtualBackgroundProps) {
  const extension =
    useRef<InstanceType<typeof VirtualBackgroundExtension>>(undefined)
  const processor = useRef<IVirtualBackgroundProcessor>(undefined)
  const isInitialized = useRef(false)

  // If we need to re-enable the virtual background again, reuse the existing VirtualBackgroundProcessor instance instead of recreating it.
  // See: https://www.npmjs.com/package/agora-extension-virtual-background
  const initialize = useCallback(async () => {
    if (!isInitialized.current) {
      const [AgoraRTC, VirtualBackgroundExtension] = await Promise.all([
        loadAgoraRTC(),
        loadVirtualBackgroundExtension(),
      ])

      extension.current = new VirtualBackgroundExtension()
      AgoraRTC.registerExtensions([extension.current])

      if (!extension.current.checkCompatibility()) {
        throw new Error('Virtual background not supported on this platform')
      }

      processor.current = extension.current.createProcessor()
      await processor.current?.init()

      isInitialized.current = true
    }
  }, [])

  useEffect(() => {
    const applyVirtualBackground = async () => {
      await initialize()

      if (videoTrack && processor.current) {
        try {
          videoTrack
            .pipe(processor.current)
            .pipe(videoTrack.processorDestination)

          if (virtualBackground === 'blur') {
            processor.current.setOptions({ type: 'blur', blurDegree: 3 })
          } else if (
            typeof virtualBackground === 'object' &&
            virtualBackground?.type === 'image' &&
            virtualBackground.url
          ) {
            const image = new Image()
            image.crossOrigin = 'anonymous' // Avoid CORS taint
            image.onload = () => {
              processor.current?.setOptions({
                type: 'img',
                source: image,
                fit: 'cover',
              })
            }
            image.src = virtualBackground.url
          }

          await processor.current.enable()
        } catch (error) {
          logError(new Error('Failed to apply virtual background'), {
            captureContext: {
              extra: {
                error: error.message,
              },
            },
          })
        }
      }
    }

    const removeVirtualBackground = async () => {
      processor.current?.unpipe()
      videoTrack?.unpipe()
      await processor.current?.disable()
    }

    if (
      virtualBackground === 'blur' ||
      (typeof virtualBackground === 'object' &&
        virtualBackground?.type === 'image' &&
        virtualBackground.url)
    ) {
      applyVirtualBackground()
    } else {
      removeVirtualBackground()
    }

    return () => {
      removeVirtualBackground()
    }
  }, [videoTrack, virtualBackground, initialize])
}
